import { type FormEvent, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { api, authClient, json } from '../../api.js';
import type { Me } from '../../App.js';
import styles from './auth.module.css';

type Guest = { id: string; displayName: string; tableNumber: string | null; placeCardUrl: string | null; claimed: boolean; hasPasskey: boolean; isAdmin: boolean };
type Step = 'search' | 'card' | 'credential';

function preload(url: string | null) {
  if (!url) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('The place card could not be loaded.'));
    image.src = url;
    if (image.complete && image.naturalWidth) resolve();
  });
}

export function Onboarding({ onComplete, pendingIdentity }: { onComplete: () => void; pendingIdentity?: Me }) {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selected, setSelected] = useState<Guest | null>(null);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [step, setStep] = useState<Step>('search');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [passkeyPending, setPasskeyPending] = useState(Boolean(pendingIdentity));
  const [passwordFallback, setPasswordFallback] = useState(false);

  useEffect(() => {
    if (!pendingIdentity) return;
    setSelected({ id: pendingIdentity.id, displayName: pendingIdentity.displayName, tableNumber: null, placeCardUrl: null, claimed: true, hasPasskey: false, isAdmin: pendingIdentity.isAdmin });
    setStep('credential');
  }, [pendingIdentity]);

  useEffect(() => {
    const term = deferredQuery.trim();
    if (!term) {
      setGuests([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    setError('');
    void api<Guest[]>(`/guests?q=${encodeURIComponent(term)}`, { signal: controller.signal })
      .then(setGuests)
      .catch(caught => {
        if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : 'Could not search the guest list.');
      })
      .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    return () => controller.abort();
  }, [deferredQuery]);

  const matches = useMemo(() => {
    const term = deferredQuery.trim().toLocaleLowerCase();
    if (!term) return [];
    return guests.filter(guest => guest.displayName.toLocaleLowerCase().includes(term)).slice(0, 12);
  }, [deferredQuery, guests]);

  async function choose(guest: Guest) {
    setBusy(true);
    setError('');
    try {
      await preload(guest.placeCardUrl);
      setSelected(guest);
      setStep('card');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The place card could not be loaded.');
    } finally {
      setBusy(false);
    }
  }

  async function passkeySignIn() {
    setBusy(true);
    setError('');
    try {
      const result = await authClient.signIn.passkey();
      if (result.error) setError(result.error.message ?? 'Passkey sign-in failed.');
      else onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Passkey sign-in failed.');
    } finally {
      setBusy(false);
    }
  }

  function continueFromCard() {
    if (!selected) return;
    setStep('credential');
    setPasswordFallback(!selected.hasPasskey);
    if (selected.claimed && selected.hasPasskey) void passkeySignIn();
  }

  async function passwordSignIn(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await api('/sessions/password', { method: 'POST', ...json({ guestId: selected.id, password }) });
      onComplete();
    } catch {
      setError('The password was not recognized.');
    } finally {
      setBusy(false);
    }
  }

  async function claim(kind: 'passkey' | 'password' | 'skip') {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const reservation = await api<{ reservationId: string; reservationToken: string }>('/claims', { method: 'POST', ...json({ guestId: selected.id }) });
      const credential = kind === 'password' ? { password } : kind === 'passkey' ? { passkeyResponse: { intent: 'register' } } : { skipCredential: true };
      const complete = await api<{ passkeyRegistrationRequired: boolean }>('/claims/complete', { method: 'POST', headers: { 'x-claim-token': reservation.reservationToken }, ...json({ reservationId: reservation.reservationId, ...credential }) });
      if (complete.passkeyRegistrationRequired) {
        setPasskeyPending(true);
        const added = await authClient.passkey.addPasskey({ name: 'Wedding album' });
        if (added.error) throw new Error(added.error.message ?? 'Could not save the passkey.');
        setPasskeyPending(false);
      }
      onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not complete setup.');
    } finally {
      setBusy(false);
    }
  }

  async function retryPasskey() {
    setBusy(true);
    setError('');
    try {
      const added = await authClient.passkey.addPasskey({ name: 'Wedding album' });
      if (added.error) {
        setError(added.error.message ?? 'Could not save the passkey. Please try again.');
        return;
      }
      setPasskeyPending(false);
      onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save the passkey. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function skipPendingPasskey() {
    setBusy(true);
    setError('');
    try {
      await api('/credentials/passkey/skip', { method: 'POST' });
      setPasskeyPending(false);
      onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not skip passkey setup.');
    } finally {
      setBusy(false);
    }
  }

  function restart() {
    setStep('search');
    setSelected(null);
    setQuery('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setPasskeyPending(false);
    setPasswordFallback(false);
  }

  return <main className={styles.paperView}>
    <section className={styles.claimSheet} aria-labelledby="claim-title">
      {step === 'search' ? <>
        <p className={styles.eyebrow}>Before you enter</p>
        <h1 id="claim-title">Find your place card</h1>
        <p className={styles.intro}>Choose your name so the album can quietly gather the photographs you appear in.</p>
        <label className={styles.searchLabel} htmlFor="guest-search">Your name</label>
        <input className={styles.searchInput} id="guest-search" type="search" placeholder="Start typing…" autoComplete="off" value={query} onChange={event => setQuery(event.target.value)} aria-controls="guest-results" aria-expanded={matches.length > 0} />
        <div className={styles.guestList} id="guest-results" aria-live="polite">
          {matches.map(guest => <button key={guest.id} disabled={busy} onClick={() => void choose(guest)}>
            <span>{guest.displayName}</span><span className={styles.dots} /><small>{guest.tableNumber ? `Table ${guest.tableNumber}` : 'Guest'}</small>
          </button>)}
        </div>
        {searching ? <p className={styles.status}>Searching the guest list…</p> : null}
        {query.trim() && !searching && !matches.length && !error ? <p className={styles.status}>No place card matches that name.</p> : null}
        {busy ? <p className={styles.status}>Loading your place card…</p> : null}
        {error ? <p role="alert" className="error">{error}</p> : null}
      </> : null}

      {step === 'card' && selected ? <>
        <p className={styles.eyebrow}>Before you enter</p>
        <h1 id="claim-title">Find your place card</h1>
        <p className={styles.intro}>Choose your name so the album can quietly gather the photographs you appear in.</p>
        <div className={styles.placeCard}>
          {selected.placeCardUrl ? <img src={selected.placeCardUrl} alt={`Place card for ${selected.displayName}`} /> : <div className={styles.cardFallback}><strong>{selected.displayName}</strong>{selected.tableNumber ? <span>Table {selected.tableNumber}</span> : null}</div>}
          <div className={styles.placeActions}>
            <button className="primary" onClick={continueFromCard}>{selected.claimed ? 'Continue' : 'That’s me'}</button>
            <button className={styles.quietButton} onClick={restart}>Not me</button>
          </div>
        </div>
      </> : null}

      {step === 'credential' && selected ? <>
        <p className={styles.eyebrow}>Before you enter</p>
        <h1 id="claim-title">{passkeyPending ? 'Finish your passkey' : selected.claimed ? 'Welcome back' : 'Claim your place'}</h1>
        <p className={styles.intro}>{passkeyPending ? 'Your place card is claimed. Save a passkey so you can return later, or finish this another time.' : selected.claimed && selected.hasPasskey && !passwordFallback ? `Use your passkey to return as ${selected.displayName}.` : selected.claimed ? `Sign in as ${selected.displayName} to return to the album.` : selected.isAdmin ? `Create a passkey for ${selected.displayName} to protect the album’s admin tools.` : `Add a private sign-in now, or skip this and set one from your account later.`}</p>
        {passkeyPending ? <>
          <button className={styles.passkeyPrimary} onClick={() => void retryPasskey()} disabled={busy}>Try passkey again</button>
          <p className={styles.passkeyNote}>Uses Face ID, Touch ID, or your device unlock.</p>
          {!selected.isAdmin ? <div className={styles.skipSetup}>
            <button className={styles.quietButton} onClick={() => void skipPendingPasskey()} disabled={busy}>Skip for now</button>
            <p>Add a password or passkey from Settings before signing out so you can return later.</p>
          </div> : null}
        </> : selected.claimed && selected.hasPasskey && !passwordFallback ? <>
          <button className={styles.passkeyPrimary} onClick={() => void passkeySignIn()} disabled={busy}>{busy ? 'Waiting for your device…' : 'Use your passkey'}</button>
          <p className={styles.passkeyNote}>Uses Face ID, Touch ID, or your device unlock.</p>
          <button className={styles.quietButton} onClick={() => { setError(''); setPasswordFallback(true); }} disabled={busy}>Use a password instead</button>
        </> : selected.claimed ? <form onSubmit={passwordSignIn} className={styles.credentialForm}>
          <label htmlFor="personal-password">Personal password</label>
          <input id="personal-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} minLength={8} required />
          <button className="primary" disabled={busy}>Sign in</button>
          {selected.hasPasskey ? <button type="button" className={styles.passkeyButton} onClick={() => { setPasswordFallback(false); void passkeySignIn(); }} disabled={busy}>Use a passkey instead</button> : null}
        </form> : <>
          <button className={styles.passkeyPrimary} onClick={() => void claim('passkey')} disabled={busy}>Create a passkey</button>
          <p className={styles.passkeyNote}>Uses Face ID, Touch ID, or your device unlock.</p>
          {!selected.isAdmin ? <>
            <div className={styles.divider}>or set a password</div>
            <form className={styles.credentialForm} onSubmit={event => { event.preventDefault(); if (password === confirmPassword) void claim('password'); else setError('The passwords do not match.'); }}>
              <label htmlFor="new-password">Password</label>
              <input id="new-password" type="password" autoComplete="new-password" value={password} onChange={event => setPassword(event.target.value)} minLength={8} maxLength={128} required />
              <label htmlFor="confirm-password">Confirm password</label>
              <input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} minLength={8} maxLength={128} required />
              <p className={styles.passwordHint}>Use at least 8 characters.</p>
              <button className="primary" disabled={busy || password.length < 8 || confirmPassword.length < 8}>Set password</button>
            </form>
            <div className={styles.skipSetup}>
              <button className={styles.quietButton} onClick={() => void claim('skip')} disabled={busy}>Skip for now</button>
              <p>You can add a password or passkey from Settings. Do that before signing out if you want to return as {selected.displayName}.</p>
            </div>
          </> : null}
        </>}
        {error ? <p role="alert" className="error">{error}</p> : null}
        {!passkeyPending ? <button className={styles.quietButton} onClick={() => setStep('card')}>Back to place card</button> : null}
      </> : null}
    </section>
  </main>;
}
